import { Component, input, output, forwardRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-checkbox',
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true
    }
  ],
  template: `
    <label 
      [for]="id() || name()"
      [class]="labelClasses"
      [attr.aria-disabled]="disabled()">
      <input
        type="checkbox"
        [id]="id() || name()"
        [name]="name()"
        [checked]="checked()"
        [disabled]="disabled()"
        [indeterminate]="indeterminate()"
        [class]="checkboxClasses"
        (change)="onChange($event)"
        (blur)="onBlur()"
        [attr.aria-checked]="indeterminate() ? 'mixed' : checked()"
        [attr.aria-label]="label() || ariaLabel()">
      @if (label()) {
        <span [class]="labelTextClasses">{{ label() }}</span>
      }
      @if (binary() && label()) {
        <span class="text-xs text-gray-500 dark:text-gray-400 ml-1">
          {{ checked() ? 'Yes' : 'No' }}
        </span>
      }
    </label>
    @if (helpText() && !hasError) {
      <span class="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
        {{ helpText() }}
      </span>
    }
    @if (hasError) {
      <span class="text-xs text-red-500 dark:text-red-400 mt-1 block font-medium">
        {{ displayError }}
      </span>
    }
  `,
  styles: []
})
export class CheckboxComponent implements ControlValueAccessor {
  label = input<string>('');
  ariaLabel = input<string>('');
  checked = signal(false);
  disabled = input<boolean>(false);
  indeterminate = input<boolean>(false);
  binary = input<boolean>(false);
  required = input<boolean>(false);
  size = input<ComponentSize>('md');
  errorMessage = input<string>('');
  helpText = input<string>('');
  id = input<string>('');
  name = input<string>('');
  class = input<string>('');

  checkedChange = output<boolean>();
  touched = signal(false);

  private onChangeFn = (value: boolean) => {};
  private onTouchedFn = () => {};

  onChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.checked;
    this.checked.set(newValue);
    this.onChangeFn(newValue);
    this.checkedChange.emit(newValue);
    this.markAsTouched();
  }

  onBlur(): void {
    this.markAsTouched();
    this.onTouchedFn();
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: boolean): void {
    this.checked.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }

  get hasError(): boolean {
    return this.touched() && !!this.errorMessage();
  }

  get displayError(): string {
    return this.errorMessage() || '';
  }

  get labelClasses(): string {
    const classes = ['flex', 'items-center', 'gap-2', 'cursor-pointer'];
    if (this.disabled()) {
      classes.push('opacity-60', 'cursor-not-allowed');
    }
    return classes.join(' ');
  }

  get checkboxClasses(): string {
    const sizeMap = {
      xs: 'w-3 h-3',
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
      xl: 'w-7 h-7'
    };

    const classes = [
      'rounded',
      'border-gray-300',
      'dark:border-gray-600',
      'text-blue-600',
      'focus:ring-2',
      'focus:ring-blue-500',
      'focus:ring-offset-2',
      'transition-colors',
      'duration-200',
      sizeMap[this.size()]
    ];

    if (this.disabled()) {
      classes.push('opacity-50', 'cursor-not-allowed');
    }

    const customClass = this.class();
    if (customClass) {
      classes.push(customClass);
    }

    return classes.join(' ');
  }

  get labelTextClasses(): string {
    return 'text-sm font-medium text-gray-700 dark:text-gray-300';
  }
}

